// Mock data for EF Core demo

export const concepts = [
  {
    id: 'code-first',
    title: 'Code-First',
    description: 'Define your data model using C# classes and let EF Core create the database schema.',
    icon: 'Code2',
    details: [
      'Define entities as C# classes',
      'Use data annotations or Fluent API for configuration',
      'Generate migrations to update database schema',
      'Maintain type safety throughout development'
    ]
  },
  {
    id: 'migrations',
    title: 'Migrations',
    description: 'Version control your database schema changes with code-based migrations.',
    icon: 'GitBranch',
    details: [
      'Create incremental database changes',
      'Apply or rollback migrations',
      'Maintain migration history',
      'Deploy schema changes safely'
    ]
  },
  {
    id: 'linq-queries',
    title: 'LINQ Queries',
    description: 'Write type-safe queries using C# LINQ syntax that translate to SQL.',
    icon: 'Database',
    details: [
      'Type-safe query expressions',
      'IntelliSense support',
      'Automatic SQL generation',
      'Optimize with compiled queries'
    ]
  },
  {
    id: 'crud-patterns',
    title: 'CRUD Patterns',
    description: 'Implement Create, Read, Update, and Delete operations efficiently.',
    icon: 'RefreshCw',
    details: [
      'Add new entities to context',
      'Query existing data',
      'Update entity properties',
      'Remove entities from database'
    ]
  },
  {
    id: 'dbcontext',
    title: 'DbContext & Tracking',
    description: 'Manage database connections and track entity state changes automatically.',
    icon: 'Activity',
    details: [
      'Connection management',
      'Change tracking',
      'Transaction support',
      'Unit of Work pattern'
    ]
  },
  {
    id: 'performance',
    title: 'Performance Basics',
    description: 'Optimize queries and avoid common pitfalls like N+1 problems.',
    icon: 'Zap',
    details: [
      'Use AsNoTracking for read-only queries',
      'Eager loading with Include()',
      'Avoid N+1 query problems',
      'Optimize with indexes'
    ]
  }
];


export const demoSteps = [
  {
    id: "setup",
    title: "Context & Goal",
    duration: "2 min",
    description: "What EF Core is, where it fits, and what we’ll demonstrate today",
    code:
        "We will cover:\n" +
        "• What EF Core does (ORM) and why teams use it\n" +
        "• The end-to-end flow: C# → EF Core → SQL → Database\n" +
        "• What you’ll be able to build after learning the basics",
    status: "completed",
  },
  {
    id: "model",
    title: "Entities & DbContext (Concept)",
    duration: "3 min",
    description: "How C# classes become tables, and how DbContext acts like a unit of work",
    code:
        "We will cover:\n" +
        "• What an Entity is (a C# class representing a table row)\n" +
        "• What DbSet means (a table-like collection)\n" +
        "• What DbContext does (tracks changes + talks to the database)\n" +
        "• How relationships are represented (navigation properties)",
    status: "active",
  },
  {
    id: "migration",
    title: "Migrations (Schema Versioning)",
    duration: "2 min",
    description: "How EF Core keeps database schema changes consistent across environments",
    code:
        "We will cover:\n" +
        "• What a migration represents (a schema change history)\n" +
        "• Why migrations matter for teams (dev/stage/prod consistency)\n" +
        "• Typical workflow: create → review → apply\n" +
        "• Safe habits: small migrations, clear naming, rollback awareness",
    status: "pending",
  },
  {
    id: "crud",
    title: "CRUD Flow (Concept)",
    duration: "4 min",
    description: "How create/read/update/delete works through change tracking and SaveChanges",
    code:
        "We will cover:\n" +
        "• Create: add entity → SaveChanges\n" +
        "• Read: query via DbSet / LINQ\n" +
        "• Update: modify properties → SaveChanges\n" +
        "• Delete: remove entity → SaveChanges\n" +
        "• What change tracking is doing behind the scenes",
    status: "pending",
  },
  {
    id: "linq",
    title: "LINQ → SQL (How Queries Work)",
    duration: "4 min",
    description: "How LINQ translates to SQL, and what to watch for in performance",
    code:
        "We will cover:\n" +
        "• How LINQ becomes SQL (translation concept)\n" +
        "• Filtering / sorting / projection basics\n" +
        "• When to use AsNoTracking\n" +
        "• How Include helps avoid N+1 queries (high-level)",
    status: "pending",
  },
];

export const sampleStudents = [
  { id: 1, name: 'Alice Johnson', email: 'alice@example.com', enrollmentDate: '2024-01-15', status: 'Active' },
  { id: 2, name: 'Bob Smith', email: 'bob@example.com', enrollmentDate: '2024-02-20', status: 'Active' },
  { id: 3, name: 'Charlie Davis', email: 'charlie@example.com', enrollmentDate: '2024-01-10', status: 'Active' },
  { id: 4, name: 'Diana Prince', email: 'diana@example.com', enrollmentDate: '2024-03-05', status: 'Inactive' }
];
